import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import type { BulkJobPhase, BulkJobStatusResponse, RowJobStatus } from "@nexus/contracts"
import { getLocalUserFacingApiMessage } from "@/lib/api-client"
import {
  cancelScripts,
  generateScripts,
  rejectOpportunities,
} from "../api/opportunities.api"
import {
  bulkJobStatusQueryOptions,
  invalidateScriptAndSetCaches,
  opportunityKeys,
  opportunitySetKeys,
  normalizedOpportunitySetTenantId,
  revealGenerationTask,
} from "@/lib/queries"
import { trackOpportunityRejected, trackOpportunityScriptGenerated } from "@/lib/analytics-events"

export type BulkActionType = "generate-scripts" | "reject"

export type BulkActionState =
  | { type: "idle" }
  | { type: "pending"; action: BulkActionType; correlationId: string }
  | { type: "polling"; action: BulkActionType; correlationId: string; items: Record<string, RowJobStatus>; total?: number; completed?: number; phase?: BulkJobPhase }
  | { type: "done"; action: BulkActionType; items: Record<string, RowJobStatus>; summary: { total: number; done: number; failed: number }; scriptCount?: number; cancelled?: boolean }
  | { type: "error"; action: BulkActionType; message: string }
  | { type: "expired"; action: BulkActionType }

const STORAGE_PREFIX = "bulk-job"

function storageKey(tenantId: string, setId: string, action: BulkActionType) {
  return `${STORAGE_PREFIX}:${tenantId}:${setId}:${action}`
}

interface StoredJob {
  correlationId: string
  rowIds: string[]
  action: BulkActionType
  timestamp: number
}

function saveJob(tenantId: string, setId: string, job: StoredJob) {
  try {
    sessionStorage.setItem(storageKey(tenantId, setId, job.action), JSON.stringify(job))
  } catch { /* quota exceeded — non-critical */ }
}

function loadJob(tenantId: string, setId: string, action: BulkActionType): StoredJob | null {
  try {
    const raw = sessionStorage.getItem(storageKey(tenantId, setId, action))
    if (!raw) return null
    return JSON.parse(raw) as StoredJob
  } catch {
    return null
  }
}

function clearJob(tenantId: string, setId: string, action: BulkActionType) {
  sessionStorage.removeItem(storageKey(tenantId, setId, action))
}

const MAX_JOB_AGE_MS = 3600 * 1000
const SYNC_REJECT_DONE_RESET_MS = 3_000

function isTerminal(status: string | undefined): boolean {
  return status === "COMPLETED" || status === "FAILED"
}

interface UseOpportunityBulkActionOptions {
  tenantId: string
  setId: string
  action: BulkActionType
}

export function useOpportunityBulkAction({ tenantId, setId, action }: UseOpportunityBulkActionOptions) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const correlationIdRef = useRef<string | null>(null)
  const rowIdsRef = useRef<string[]>([])
  const lastSeenCompletedRef = useRef<number>(0)

  // Synchronous reject result — bypasses Amber Path polling entirely
  const [syncDone, setSyncDone] = useState<BulkActionState & { type: "done" | "idle" }>({ type: "idle" })

  // Reactive mirror of correlationIdRef — drives re-renders when job state changes.
  // Must be state (not just a ref) so hasActiveJob updates the UI correctly.
  const [activeCorrelationId, setActiveCorrelationId] = useState<string | null>(null)

  // Sync job state from sessionStorage whenever tenantId/setId/action changes.
  // Clears stale state when switching to a set with no active job.
  useEffect(() => {
    if (action === "reject") return
    const stored = loadJob(tenantId, setId, action)
    if (stored && Date.now() - stored.timestamp <= MAX_JOB_AGE_MS) {
      lastSeenCompletedRef.current = 0
      correlationIdRef.current = stored.correlationId
      rowIdsRef.current = stored.rowIds
      setActiveCorrelationId(stored.correlationId)
    } else {
      if (stored) clearJob(tenantId, setId, action)
      lastSeenCompletedRef.current = 0
      correlationIdRef.current = null
      rowIdsRef.current = []
      setActiveCorrelationId(null)
    }
  }, [tenantId, setId, action])

  const hasActiveJob = activeCorrelationId !== null

  const statusQuery = useQuery<BulkJobStatusResponse | null>({
    ...bulkJobStatusQueryOptions(activeCorrelationId ?? ""),
    enabled: hasActiveJob && action !== "reject",
  })

  const state = useMemo((): BulkActionState => {
    // Reject is synchronous — use local state
    if (action === "reject") return syncDone

    if (!hasActiveJob) return { type: "idle" }

    if (statusQuery.isError) {
      return { type: "error", action, message: statusQuery.error?.message ?? "Polling failed" }
    }

    if (statusQuery.data === null) {
      return { type: "expired", action }
    }

    const data = statusQuery.data
    if (!data) {
      return { type: "pending", action, correlationId: activeCorrelationId! }
    }

    if (isTerminal(data.status)) {
      const items = data.items ?? {}
      const total = Object.keys(items).length || rowIdsRef.current.length
      const done = Object.values(items).filter((s) => s === "done").length
      const failed = Object.values(items).filter((s) => s === "failed").length
      // `items` is keyed per opportunity, so its size is the opportunity count
      // (e.g. 1), not the number of scripts produced. The engine reports the
      // real script count via `completed`/`total`; surface that in the success
      // toast so "1 opportunity → 50 scripts" reads as "50 scripts generated".
      const scriptCount = data.completed ?? data.total ?? total
      return { type: "done", action, items, summary: { total, done, failed }, scriptCount, cancelled: data.cancelled }
    }

    return {
      type: "polling",
      action,
      correlationId: activeCorrelationId!,
      items: data.items ?? {},
      total: data.total,
      completed: data.completed,
      phase: data.phase,
    }
  }, [hasActiveJob, statusQuery.data, statusQuery.isError, statusQuery.error, action, tenantId, setId, syncDone, activeCorrelationId])

  useEffect(() => {
    if (state.type !== "polling" || action !== "generate-scripts") return
    const completed = state.completed ?? 0
    if (completed > lastSeenCompletedRef.current) {
      lastSeenCompletedRef.current = completed
      invalidateScriptAndSetCaches(queryClient, { setId, tenantId })
    }
  }, [state, action, setId, tenantId, queryClient])

  useEffect(() => {
    if (state.type === "done") {
      if (action === "reject") {
        const timer = setTimeout(() => setSyncDone({ type: "idle" }), SYNC_REJECT_DONE_RESET_MS)
        return () => clearTimeout(timer)
      }

      // Script completion UX lives in the unified task-progress sidebar.
      if (action === "generate-scripts") {
        invalidateScriptAndSetCaches(queryClient, { setId, tenantId })
      }

      clearJob(tenantId, setId, action)
      correlationIdRef.current = null
      setActiveCorrelationId(null)
    } else if (state.type === "expired") {
      toast.warning("Job expired — results unavailable")
      clearJob(tenantId, setId, action)
      correlationIdRef.current = null
      setActiveCorrelationId(null)
    }
  }, [state.type, tenantId, setId, action, state, queryClient])

  const mutation = useMutation({
    mutationFn: async (params: { opportunityIds: string[]; reason?: string }) => {
      if (action === "reject") {
        const result = await rejectOpportunities({
          tenantId,
          opportunityIds: params.opportunityIds,
          reason: params.reason ?? "",
        })
        return { sync: true as const, updated: result.updated, opportunityIds: params.opportunityIds }
      }

      if (hasActiveJob) {
        throw new Error(`A ${action} job is already in progress`)
      }

      const result = await generateScripts({
        tenantId,
        setId,
        opportunityIds: params.opportunityIds,
      })

      return { sync: false as const, correlationId: result.correlationId, opportunityIds: params.opportunityIds }
    },
    onSuccess: (data, variables) => {
      if (data.sync) {
        const total = data.opportunityIds.length
        const done = data.updated
        const failed = total - done
        const items: Record<string, RowJobStatus> = {}
        for (const id of data.opportunityIds) {
          items[id] = done > 0 ? "done" : "failed"
        }
        setSyncDone({ type: "done", action, items, summary: { total, done, failed } })

        if (failed > 0) {
          toast.error(`${done} rejected, ${failed} failed`)
        } else {
          toast.success(`${total} opportunities rejected`)
        }

        // Track rejection analytics — fire one event per rejected opportunity
        for (const oppId of data.opportunityIds) {
          trackOpportunityRejected({
            opportunity_id: oppId,
            opportunity_set_id: setId,
            topic_title: '',
            rejection_reason_length: (variables.reason ?? '').length,
          })
        }

        void queryClient.invalidateQueries({ queryKey: opportunityKeys.all })
        void queryClient.invalidateQueries({
          queryKey: opportunitySetKeys.list(normalizedOpportunitySetTenantId(tenantId)),
        })
        return
      }

      correlationIdRef.current = data.correlationId
      rowIdsRef.current = data.opportunityIds
      setActiveCorrelationId(data.correlationId)
      lastSeenCompletedRef.current = 0
      saveJob(tenantId, setId, { correlationId: data.correlationId, rowIds: data.opportunityIds, action, timestamp: Date.now() })

      trackOpportunityScriptGenerated({
        opportunity_ids: data.opportunityIds,
        opportunity_count: data.opportunityIds.length,
        opportunity_set_id: setId,
        trigger_source: 'selection_tray',
      })

      revealGenerationTask(queryClient)

      queryClient.invalidateQueries({ queryKey: opportunityKeys.bulkStatus(data.correlationId) })
    },
    onError: (error) => {
      const description = getLocalUserFacingApiMessage(error);
      if (!description) return;
      toast.error("Request failed", { description })
    },
    meta: { skipGlobalError: true },
  })

  const cancelMutation = useMutation({
    mutationFn: () => {
      if (!activeCorrelationId) throw new Error('No active job to cancel')
      return cancelScripts({ correlationId: activeCorrelationId, tenantId })
    },
    onError: (error) => {
      const description = getLocalUserFacingApiMessage(error);
      if (!description) return;
      toast.error("Failed to cancel", { description })
    },
    meta: { skipGlobalError: true },
  })

  // True from the moment the user confirms cancel until the engine flips the
  // correlation status to COMPLETED + cancelled. The engine only re-checks the
  // cancel flag between waves of in-flight Claude calls, so this gap can last
  // tens of seconds — without this signal the UI keeps showing "Generating…"
  // and the cancel feels broken.
  //
  // The local mutation flag covers the same-session case; the server-derived
  // `cancelRequested` flag (read by GET /status) covers page refreshes where
  // the mutation state is gone but the cache entry survives.
  const inFlight = state.type === "pending" || state.type === "polling"
  const cancelRequestedRemote = statusQuery.data?.cancelRequested === true
  const isCancelling =
    cancelMutation.isPending ||
    (cancelMutation.isSuccess && inFlight) ||
    (cancelRequestedRemote && inFlight)

  const cancel = useCallback(() => {
    cancelMutation.mutate()
  }, [cancelMutation])

  const execute = useCallback(
    (params: { opportunityIds: string[]; reason?: string }) => {
      // Clear any cancel state left over from a prior job so isCancelling
      // doesn't latch true on the next run.
      cancelMutation.reset()
      mutation.mutate(params)
    },
    [mutation, cancelMutation],
  )

  const reset = useCallback(() => {
    if (action === "reject") {
      setSyncDone({ type: "idle" })
      return
    }
    if (activeCorrelationId) {
      queryClient.removeQueries({ queryKey: opportunityKeys.bulkStatus(activeCorrelationId) })
    }
    correlationIdRef.current = null
    rowIdsRef.current = []
    setActiveCorrelationId(null)
    clearJob(tenantId, setId, action)
  }, [queryClient, tenantId, setId, action, activeCorrelationId])

  const failedCount = useMemo(() => {
    if (state.type === "done") return state.summary.failed
    if (state.type === "polling") return Object.values(state.items).filter((s) => s === "failed").length
    return 0
  }, [state])

  return { execute, cancel, state, reset, isSubmitting: mutation.isPending, isCancelling, failedCount }
}
